import { useState, useEffect, useCallback } from "react";
import { videoAPI } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { ScrollArea } from "./ui/scroll-area";
import { Play, CheckCircle, X, ChevronLeft, ChevronRight, BookOpen } from "lucide-react";

const TUTORIALS = [
  {
    title: "Welcome to the Unstoppable Company Game",
    description: "An introduction to the USG methodology and how it helps your leadership team make strategic AI decisions across every department.",
    placeholder: "Loom Embed — Video 1: Introduction",
  },
  {
    title: "Understanding Automate, Augment & Protect",
    description: "Deep dive into the three decision types. Learn when to automate, when to augment with AI, and when to protect human-only workflows.",
    placeholder: "Loom Embed — Video 2: Decision Framework",
  },
  {
    title: "Navigating the Game Board",
    description: "How to use the game board interface: departments, stalls, making decisions, adding notes, and using AI suggestions.",
    placeholder: "Loom Embed — Video 3: Board Walkthrough",
  },
  {
    title: "Collaborating with Your Team",
    description: "Best practices for working through the USG with your team — whether live or asynchronously. Tips for productive discussions.",
    placeholder: "Loom Embed — Video 4: Team Collaboration",
  },
  {
    title: "From Decisions to Action",
    description: "How to interpret your results, export use cases, and build your 90-day AI roadmap using the analytics and export tools.",
    placeholder: "Loom Embed — Video 5: Results & Next Steps",
  },
];

export const TutorialVideos = ({ open, onOpenChange, boardId }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [watched, setWatched] = useState([]);

  const fetchProgress = useCallback(async () => {
    if (!boardId) return;
    try {
      const res = await videoAPI.getProgress(boardId);
      setWatched(res.data.watched || []);
    } catch {
      // silent
    }
  }, [boardId]);

  useEffect(() => {
    if (open) fetchProgress();
  }, [open, fetchProgress]);

  const markWatched = async (idx) => {
    if (watched.includes(idx)) return;
    try {
      await videoAPI.markWatched(boardId, idx);
      setWatched((prev) => [...prev, idx]);
    } catch {
      // silent
    }
  };

  const handleDismiss = async () => {
    try {
      await videoAPI.dismiss(boardId);
    } catch {
      // silent
    }
    onOpenChange(false);
  };

  const current = TUTORIALS[activeIndex];
  const allWatched = watched.length === TUTORIALS.length;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[560px] p-0 bg-[#F8FAFC]" data-testid="tutorial-videos-panel">
        <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-md border-b border-slate-200 px-6 py-4">
          <SheetHeader className="text-left">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-indigo-500" />
                <SheetTitle className="font-heading text-lg text-slate-900">Tutorial Videos</SheetTitle>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs text-slate-500">
                  {watched.length}/{TUTORIALS.length} watched
                </Badge>
                <Button variant="ghost" size="sm" onClick={handleDismiss} className="h-8 w-8 p-0">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </SheetHeader>
        </div>

        <ScrollArea className="h-[calc(100vh-80px)]">
        <div className="p-6 space-y-4">
          {/* Video Player Area */}
          <div className="bg-slate-900 rounded-xl overflow-hidden aspect-video flex items-center justify-center relative">
            <div className="text-center space-y-3 p-6">
              <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto backdrop-blur-sm">
                <Play className="w-7 h-7 text-white ml-1" />
              </div>
              <p className="text-white/80 text-sm font-medium">{current.placeholder}</p>
              <p className="text-white/40 text-xs">Paste Loom embed code here later</p>
            </div>
            <div className="absolute top-3 right-3">
              <Badge className="bg-white/10 text-white/70 border-white/20 text-[10px] backdrop-blur-sm">
                {activeIndex + 1} / {TUTORIALS.length}
              </Badge>
            </div>
          </div>

          {/* Video Info */}
          <div>
            <h3 className="font-heading font-semibold text-base text-slate-900">{current.title}</h3>
            <p className="text-sm text-slate-500 mt-1 leading-relaxed">{current.description}</p>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              disabled={activeIndex === 0}
              onClick={() => setActiveIndex(activeIndex - 1)}
              className="h-9"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            <Button
              size="sm"
              data-testid={`mark-video-watched-${activeIndex}`}
              onClick={() => markWatched(activeIndex)}
              disabled={watched.includes(activeIndex)}
              className={`h-9 ${watched.includes(activeIndex) ? "bg-emerald-600" : "bg-indigo-600 hover:bg-indigo-700"} text-white`}
            >
              {watched.includes(activeIndex) ? (
                <><CheckCircle className="w-4 h-4 mr-1" />Watched</>
              ) : (
                <><CheckCircle className="w-4 h-4 mr-1" />Mark as Watched</>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={activeIndex === TUTORIALS.length - 1}
              onClick={() => setActiveIndex(activeIndex + 1)}
              className="h-9"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>

          {/* Video List */}
          <div className="border-t border-slate-200 pt-4">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 block">
              All Videos
            </Label>
              <div className="space-y-1.5">
                {TUTORIALS.map((vid, idx) => (
                  <div
                    key={idx}
                    data-testid={`video-item-${idx}`}
                    onClick={() => setActiveIndex(idx)}
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                      activeIndex === idx
                        ? "bg-indigo-50 border border-indigo-200"
                        : "hover:bg-slate-50 border border-transparent"
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                      watched.includes(idx) ? "bg-emerald-100" : "bg-slate-100"
                    }`}>
                      {watched.includes(idx) ? (
                        <CheckCircle className="w-4 h-4 text-emerald-600" />
                      ) : (
                        <Play className="w-3.5 h-3.5 text-slate-400 ml-0.5" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-medium truncate ${
                        activeIndex === idx ? "text-indigo-700" : "text-slate-700"
                      }`}>
                        {vid.title}
                      </div>
                      <div className="text-[10px] text-slate-400 truncate">{vid.description}</div>
                    </div>
                  </div>
                ))}
              </div>
          </div>

          {allWatched && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
              <CheckCircle className="w-5 h-5 text-emerald-600 mx-auto mb-1" />
              <p className="text-sm font-medium text-emerald-800">All tutorials complete!</p>
              <p className="text-xs text-emerald-600 mt-0.5">You can revisit these anytime from the toolbar.</p>
            </div>
          )}
        </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

const Label = ({ className, children, ...props }) => (
  <label className={className} {...props}>{children}</label>
);

export default TutorialVideos;
